import { useEffect, useMemo, useState } from 'react';
import type { Editor } from '../editor';
import type { BlockDef, BlockRefEntity, CadEntity } from '../model/entity';
import { newEntityId } from '../model/document';
import { BLOCK_CATEGORIES } from '../blocks/standardLibrary';
import { captureSelectionAsBlock } from '../blocks/catalog';
import { registerBlock, blockRowToCatalogBlock } from '../../data/blockRepo';
import { listLayerTemplates, type LayerTemplateRow } from '../../data/repo';
import type { CadOp } from '../ops/operation';
import { Modal, ModalCancel } from '../../shell/Modal';

/**
 * 선택한 도형을 새 블록으로 등록한다 (P4-06 확장).
 *
 * ★ CAD관리자·시스템관리자만 열 수 있는 화면(SidePanel Properties 쪽 role 게이트)
 *   이므로, 기존 "블록 수정 제안 → 다른 관리자 승인" 과 달리 여기서는 바로
 *   등록한다. RLS(`cad_block_insert`)도 admin scope=global 직접 insert 를
 *   이미 허용하고 있다.
 *
 * ★ 등록 후에는 방금까지 도면에 있던 원본 도형(선·원 등)을 지우고, 그 자리에
 *   새 블록 참조 하나를 놓는다 — 도형만 라이브러리에 추가되고 도면은 그대로면
 *   "방금 그린 게 블록이 됐다"는 느낌이 안 온다. 물량 산출·EXPLODE 같은 다른
 *   기능도 blockref 라야 인식한다.
 */
export function RegisterBlockModal({
  editor,
  entities,
  onClose,
}: {
  editor: Editor;
  entities: CadEntity[];
  onClose: () => void;
}) {
  const captured = useMemo(
    () => captureSelectionAsBlock(entities, editor.scene.blocks),
    [entities, editor]
  );

  const [name, setName] = useState('');
  const [categoryCode, setCategoryCode] = useState(BLOCK_CATEGORIES[0]?.code ?? '');
  const [defaultLayer, setDefaultLayer] = useState(entities[0]?.layer ?? editor.currentLayer);
  const [layers, setLayers] = useState<LayerTemplateRow[]>([]);
  const [layersLoading, setLayersLoading] = useState(true);
  const [resizable, setResizable] = useState(false);
  const [heightMm, setHeightMm] = useState('');
  const [frontMm, setFrontMm] = useState('');
  const [sideMm, setSideMm] = useState('');
  const [rearMm, setRearMm] = useState('');
  const [power, setPower] = useState('');
  const [water, setWater] = useState(false);
  const [drain, setDrain] = useState(false);
  const [heatLoadW, setHeatLoadW] = useState('');
  const [standardKrw, setStandardKrw] = useState('');
  const [installKrw, setInstallKrw] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const areaM2 = ((captured.widthMm * captured.depthMm) / 1_000_000).toFixed(2);

  /*
   * 레이어 드롭박스는 반드시 DB(cad_layer_template)에서 읽는다 — 편집기 부팅용
   * STANDARD_LAYERS 상수(model/document.ts)와는 손으로 맞춰 둔 별개의 소스라
   * 어긋날 수 있다. 여기서 상수를 쓰면 cad_block.default_layer FK 가 DB에 없는
   * 코드를 거부해서 "블록 등록: ... violates foreign key constraint
   * cad_block_default_layer_fkey" 로 실패한다 — 실제로 겪은 문제.
   */
  useEffect(() => {
    let alive = true;
    listLayerTemplates()
      .then((rows) => {
        if (!alive) return;
        setLayers(rows);
        // 지금 고른 레이어가 DB에 없으면(레이어 코드 드리프트) 첫 유효한 값으로 바꾼다
        setDefaultLayer((prev) => (rows.some((r) => r.code === prev) ? prev : (rows[0]?.code ?? prev)));
      })
      .catch(() => {
        /* 조용히 실패 — 아래 disabled 처리가 등록 버튼을 막아 FK 위반을 예방한다 */
      })
      .finally(() => {
        if (alive) setLayersLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function submit() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const row = await registerBlock({
        name: name.trim(),
        categoryCode,
        defaultLayer,
        widthMm: captured.widthMm,
        depthMm: captured.depthMm,
        heightMm: strToNum(heightMm),
        clearance: {
          frontMm: strToNum(frontMm) ?? undefined,
          sideMm: strToNum(sideMm) ?? undefined,
          rearMm: strToNum(rearMm) ?? undefined,
        },
        utility: {
          power: strToNum(power),
          water,
          drain,
          heatLoadW: strToNum(heatLoadW) ?? undefined,
        },
        cost: {
          standardKrw: strToNum(standardKrw) ?? undefined,
          installKrw: strToNum(installKrw) ?? undefined,
        },
        geometry: captured.entities,
        resizable,
      });

      const catalogBlock = blockRowToCatalogBlock(row);
      if (catalogBlock) editor.addCustomBlock(catalogBlock);

      const def: BlockDef = {
        code: row.blockCode,
        name: row.name,
        entities: captured.entities,
        clearance: row.clearance,
        widthMm: row.widthMm ?? undefined,
        depthMm: row.depthMm ?? undefined,
      };

      const ref: BlockRefEntity = {
        id: newEntityId(),
        type: 'blockref',
        layer: defaultLayer,
        blockCode: row.blockCode,
        at: captured.origin,
        rotation: 0,
        scale: 1,
        attributes: {},
      };

      const ops: CadOp[] = [
        { kind: 'delete', command: 'BLOCKIFY', entities },
        {
          kind: 'meta',
          command: 'BLOCKIFY',
          before: { blocks: editor.scene.doc.blocks.map((b) => ({ ...b })) },
          after: { blocks: [...editor.scene.doc.blocks.map((b) => ({ ...b })), def] },
        },
        { kind: 'add', command: 'BLOCKIFY', entities: [ref] },
      ];

      editor.scene.commit('BLOCKIFY', ops);
      editor.selectOnly(ref.id);
      editor.write(`${row.name} 을(를) 블록으로 등록했습니다.`);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title="선택한 도형을 블록으로 등록"
      desc={`지금 그려진 모양(도형 ${entities.length}개) 그대로 블록으로 등록하고, 도면의 원본은 새 블록 참조로 바뀝니다.`}
      onClose={onClose}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={busy || !name.trim() || layersLoading || layers.length === 0}
            title={layers.length === 0 && !layersLoading ? '레이어 목록을 불러오지 못했습니다' : undefined}
          >
            {busy ? '등록 중…' : layersLoading ? '레이어 확인 중…' : '등록'}
          </button>
        </>
      }
    >
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label className="field">
          <span className="field__label">이름 *</span>
          <input
            className="input"
            value={name}
            autoFocus
            placeholder="예: 계산대 유닛"
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">분류</span>
          <select className="select" value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)}>
            {BLOCK_CATEGORIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.icon} {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">기본 레이어</span>
          <select
            className="select"
            value={defaultLayer}
            disabled={layersLoading}
            // 목록이 늦게 와서 값이 저절로 바뀐다 — '바뀐 내용' 판정에서 뺀다
            data-oui-skip=""
            onChange={(e) => setDefaultLayer(e.target.value)}
          >
            {layersLoading && <option value={defaultLayer}>불러오는 중…</option>}
            {!layersLoading && layers.length === 0 && <option value={defaultLayer}>레이어 없음</option>}
            {layers.map((l) => (
              <option key={l.code} value={l.code}>
                {l.code} — {l.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">높이(mm, 선택)</span>
          <input
            className="input"
            inputMode="numeric"
            value={heightMm}
            onChange={(e) => setHeightMm(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">폭(mm) — 그린 크기 그대로</span>
          <input className="input" value={captured.widthMm} disabled />
        </label>
        <label className="field">
          <span className="field__label">깊이(mm) — 그린 크기 그대로</span>
          <input className="input" value={captured.depthMm} disabled />
        </label>
        <label className="field">
          <span className="field__label">면적(㎡) — 자동계산</span>
          <input className="input" value={areaM2} disabled />
        </label>
        <div className="field">
          <span className="field__label">크기 조정</span>
          <div style={{ display: 'flex', gap: 14, paddingTop: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400 }}>
              <input
                type="radio"
                name="rb-resizable"
                checked={!resizable}
                onChange={() => setResizable(false)}
              />
              고정 크기
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400 }}>
              <input
                type="radio"
                name="rb-resizable"
                checked={resizable}
                onChange={() => setResizable(true)}
              />
              변경 가능
            </label>
          </div>
          <span className="field__hint">
            변경 가능으로 등록하면, 이 블록을 넣을 때(INSERT) 폭을 새로 입력해 크기를 바꿀 수
            있습니다.
          </span>
        </div>

        <label className="field">
          <span className="field__label">이격 · 전면(mm)</span>
          <input className="input" inputMode="numeric" value={frontMm} onChange={(e) => setFrontMm(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">이격 · 측면(mm)</span>
          <input className="input" inputMode="numeric" value={sideMm} onChange={(e) => setSideMm(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">이격 · 후면(mm)</span>
          <input className="input" inputMode="numeric" value={rearMm} onChange={(e) => setRearMm(e.target.value)} />
        </label>
        <div />

        <label className="field">
          <span className="field__label">전원(V)</span>
          <input className="input" inputMode="numeric" value={power} onChange={(e) => setPower(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">발열(W)</span>
          <input
            className="input"
            inputMode="numeric"
            value={heatLoadW}
            onChange={(e) => setHeatLoadW(e.target.value)}
          />
        </label>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={water} onChange={(e) => setWater(e.target.checked)} />
          <span className="field__label">급수 필요</span>
        </label>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={drain} onChange={(e) => setDrain(e.target.checked)} />
          <span className="field__label">배수 필요</span>
        </label>

        <label className="field">
          <span className="field__label">단가 · 표준(원, 선택)</span>
          <input
            className="input"
            inputMode="numeric"
            value={standardKrw}
            onChange={(e) => setStandardKrw(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">단가 · 설치(원, 선택)</span>
          <input
            className="input"
            inputMode="numeric"
            value={installKrw}
            onChange={(e) => setInstallKrw(e.target.value)}
          />
        </label>
      </div>
    </Modal>
  );
}

function strToNum(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}
